/**
 * clientRecordApi — the LIVE aggregate read behind the client record
 * page (PR-C2).
 *
 * ONE fetch per record view:
 *   GET /api/t/{slug}/v1/clients/{clientId}/record → { record: ClientRecordSnapshot }
 *
 * The page calls useClientRecord ONCE and passes the snapshot down —
 * Overview + Care record + People render the SAME payload (one read =
 * one phi_access_log row server-side; no per-tab fetches). The contract
 * type stays in src/lib/clientRecord.ts (frozen seam); this file is only
 * the transport + react-query hook (house pattern: workspace.ts /
 * notes.ts fetch semantics).
 */

import { useQuery, type QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import type { ClientRecordSnapshot } from './clientRecord';

export function clientRecordKey(slug: string, clientId: string): readonly unknown[] {
  return ['clientRecord', slug, clientId] as const;
}

/** The record the page already read, from the query cache; never
 *  fetched. Undefined when the cache does not hold it. */
export function cachedClientRecord(
  queryClient: QueryClient,
  slug: string,
  clientId: string,
): ClientRecordSnapshot | undefined {
  return queryClient.getQueryData<ClientRecordSnapshot>(clientRecordKey(slug, clientId));
}

async function fetchClientRecord(
  slug: string,
  clientId: string,
  signal?: AbortSignal,
): Promise<ClientRecordSnapshot> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/record`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `GET ${path} → HTTP ${res.status}`);
  }
  const payload = (await res.json()) as { record: ClientRecordSnapshot };
  return payload.record;
}

export interface UseClientRecordResult {
  record: ClientRecordSnapshot | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * The one aggregate query. `enabled` gates the fetch on the caller's
 * read_clinical_record capability — a denied role fires NO request.
 */
export function useClientRecord(
  slug: string,
  clientId: string,
  opts: { enabled?: boolean } = {},
): UseClientRecordResult {
  const query = useQuery<ClientRecordSnapshot>({
    queryKey: clientRecordKey(slug, clientId),
    queryFn: ({ signal }) => fetchClientRecord(slug, clientId, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && clientId !== '',
  });
  return {
    record: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
